import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";

import {addDays, addMilliseconds, endOfDay, isAfter, isBefore, setSeconds} from "date-fns";

import {TranslateService} from "@ngx-translate/core";

import {BaseComponent} from "@shared/components/base-component";

import {LinkDateRangeModel} from "@data-sharing/routes/link-view/link-date-range.model";

@Component({
  selector: 'dsm-link-activity',
  templateUrl: './link-activity.component.html',
})
export class LinkActivityComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() isViewMode = false;
  @Input() readonlyDateRange?: LinkDateRangeModel;

  @Output() dateChanged = new EventEmitter<LinkDateRangeModel>();

  private readonly requiredErrorKey = 'required';
  private readonly startDateAfterEndDateErrorKey = 'startDateAfterEndDate';
  private readonly endDateBeforeStartDateErrorKey = 'endDateBeforeStartDate';
  readonly endOfNextDay = addMilliseconds(endOfDay(addDays(new Date(), 1)), -100);//-100 milliseconds to not round date to 0 next day by api at save

  isAlwaysSelected = false;
  isDateRangeButtonSelected = true;
  isAlwaysButtonVisible = true;
  isDateRangeButtonVisible = true;

  fieldIsRequiredErrorMessage = '';
  startDateAfterEndDateMessage = '';
  endDateBeforeStartDateMessage = '';

  startDateCtrl = new FormControl(setSeconds(new Date(), 0), [Validators.required, this.startDateAfterEndDateValidator()]);
  endDateCtrl = new FormControl<Date | undefined>(this.endOfNextDay, [this.endDateBeforeStartDateValidator()]);

  form = this.formBuilder.group({
    startDate: this.startDateCtrl,
    endDate: this.endDateCtrl
  });

  minDate = new Date();

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService
  ) {
    super();
    this.setTranslations();
    this.subToFormsValueChanges();
  }

  ngAfterViewInit(): void {
    this.emitDateRangeDateChanged();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isViewMode)
      this.setViewMode();
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  onAlways() {
    if (this.isViewMode)
      return;

    this.isAlwaysSelected = !this.isAlwaysSelected;
    this.isDateRangeButtonSelected = !this.isAlwaysSelected;

    const dateRangeModel = {} as LinkDateRangeModel;
    dateRangeModel.startDate = this.startDateCtrl.value ?? undefined;
    dateRangeModel.endDate = undefined;

    this.dateChanged.emit(dateRangeModel);
  }

  onDateRangeSelected() {
    if (this.isViewMode)
      return;

    this.isDateRangeButtonSelected = !this.isDateRangeButtonSelected;
    this.isAlwaysSelected = !this.isDateRangeButtonSelected;

    this.emitDateRangeDateChanged();
  }

  getErrorMessage(control: AbstractControl): string {
    if (control.hasError(this.requiredErrorKey))
      return this.fieldIsRequiredErrorMessage;

    if (control.hasError(this.startDateAfterEndDateErrorKey))
      return this.startDateAfterEndDateMessage;

    if (control.hasError(this.endDateBeforeStartDateErrorKey))
      return this.endDateBeforeStartDateMessage;

    return "";
  }

  getErrorMessageElementId(controlName: string, control: AbstractControl): string {
    let errorName = '';

    if (control.hasError(this.requiredErrorKey))
      errorName = this.requiredErrorKey;

    else if (control.hasError(this.startDateAfterEndDateErrorKey))
      errorName = this.startDateAfterEndDateErrorKey;

    else if (control.hasError(this.endDateBeforeStartDateErrorKey))
      errorName = this.endDateBeforeStartDateErrorKey;

    return `${controlName}-error-${errorName}`;
  }

  private emitDateRangeDateChanged() {
    const dateRangeModel = {} as LinkDateRangeModel;
    dateRangeModel.startDate = this.startDateCtrl.value ?? undefined;
    dateRangeModel.endDate = this.endDateCtrl.value ?? undefined;

    this.dateChanged.emit(dateRangeModel);
  }

  private setTranslations() {
    this.translateService.get('errors.field-is-required').subscribe(t => this.fieldIsRequiredErrorMessage = t);
    this.translateService.get('errors.start-date-after-end-date').subscribe(t => this.startDateAfterEndDateMessage = t);
    this.translateService.get('errors.end-date-before-start-date').subscribe(t => this.endDateBeforeStartDateMessage = t);
  }

  private setViewMode() {
    const isAlways = this.readonlyDateRange?.endDate === undefined;
    this.isAlwaysSelected = isAlways;
    this.isDateRangeButtonSelected = !isAlways;

    if (!isAlways) {
      this.startDateCtrl.setValue(this.readonlyDateRange!.startDate!);
      this.endDateCtrl.setValue(this.readonlyDateRange!.endDate!);
    }

    this.isAlwaysButtonVisible = this.isAlwaysSelected;
    this.isDateRangeButtonVisible = this.isDateRangeButtonSelected;
  }

  private subToFormsValueChanges() {
    this.addToUnsubscribe(
      this.startDateCtrl.valueChanges.subscribe(newDate => this.onStartDateChanged(newDate)));

    this.addToUnsubscribe(
      this.endDateCtrl.valueChanges.subscribe(newDate => this.onEndDateChanged(newDate)));
  }

  private onStartDateChanged(newDate: Date | null) {
    if (newDate) {
      newDate = setSeconds(newDate, 59);
      this.startDateCtrl.setValue(newDate, {emitEvent: false});
      this.endDateCtrl.updateValueAndValidity({onlySelf: true, emitEvent: false});
    }
    const dateRangeModel = {} as LinkDateRangeModel;
    dateRangeModel.startDate = newDate ?? undefined;
    dateRangeModel.endDate = this.endDateCtrl.value ?? undefined;
    this.dateChanged.emit(dateRangeModel);
  }

  private onEndDateChanged(newDate: Date | null | undefined) {
    if (newDate) {
      newDate = setSeconds(newDate, 59);
      this.endDateCtrl.setValue(newDate, {emitEvent: false});
      this.startDateCtrl.updateValueAndValidity({onlySelf: true, emitEvent: false});
    }
    const dateRangeModel = {} as LinkDateRangeModel;
    dateRangeModel.startDate = this.startDateCtrl.value ?? undefined;
    dateRangeModel.endDate = this.endDateCtrl.value ?? undefined;
    this.dateChanged.emit(dateRangeModel);
  }

  private startDateAfterEndDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const start = this.startDateCtrl?.value;
      const end = this.endDateCtrl?.value;

      return start && end && isAfter(start, end) ? {startDateAfterEndDate: true} : null;
    };
  }

  private endDateBeforeStartDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const start = this.startDateCtrl?.value;
      const end = this.endDateCtrl?.value;

      return start && end && isBefore(end, start) ? {endDateBeforeStartDate: true} : null;
    };
  }

}
